import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  MessageSquare,
  GitBranch,
  Activity,
  Bot,
  BookOpen,
  Settings,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  LogOut,
  User,
  ShieldCheck,
} from 'lucide-react';
import {
  selectSidebarOpen,
  selectThemeMode,
  toggleSidebar,
  toggleTheme,
} from '@/store/slices/uiSlice';
import { useAuth } from '@/hooks/useAuth';
import { ADMIN_ROLES, filterByRoles } from '@/utils/rbac';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import type { RootState, AppDispatch } from '@/store/store';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  requiredRoles?: string[];
}

const platformNav: NavItem[] = [
  { label: 'Chat', path: '/chat', icon: <MessageSquare size={20} /> },
  { label: 'Pipelines', path: '/pipelines', icon: <GitBranch size={20} /> },
  {
    label: 'Monitoring',
    path: '/monitoring',
    icon: <Activity size={20} />,
    requiredRoles: ADMIN_ROLES,
  },
  {
    label: 'QC Dashboard',
    path: '/qc',
    icon: <ShieldCheck size={20} />,
    requiredRoles: ADMIN_ROLES,
  },
];

const buildNav: NavItem[] = [
  { label: 'Agent Studio', path: '/agent-studio', icon: <Bot size={20} /> },
  { label: 'Knowledge', path: '/knowledge', icon: <BookOpen size={20} /> },
  {
    label: 'Admin',
    path: '/admin',
    icon: <Settings size={20} />,
    requiredRoles: ADMIN_ROLES,
  },
];

export function Sidebar() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const isOpen = useSelector((state: RootState) => selectSidebarOpen(state));
  const themeMode = useSelector((state: RootState) => selectThemeMode(state));
  const { user, logout } = useAuth();
  const [userAnchor, setUserAnchor] = useState<HTMLElement | null>(null);

  const collapsed = !isOpen;
  const width = collapsed ? 72 : 260;

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleNav = (path: string) => {
    void navigate(path);
  };

  const handleLogout = async () => {
    await logout();
    void navigate('/login');
  };

  const renderNavSection = (title: string, items: NavItem[]) => (
    <Box sx={{ mb: 1 }}>
      {!collapsed && (
        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 1,
            display: 'block',
            color: 'text.disabled',
            fontSize: '0.6875rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
      )}
      <List disablePadding>
        {items.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
              <Tooltip title={collapsed ? item.label : ''} placement="right">
                <ListItemButton
                  onClick={() => {
                    handleNav(item.path);
                  }}
                  sx={{
                    borderRadius: 'var(--radius-md)',
                    minHeight: 40,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 1.5 : 2,
                    mb: 0.25,
                    borderLeft: active ? '3px solid' : '3px solid transparent',
                    borderColor: active ? 'primary.main' : 'transparent',
                    bgcolor: active ? 'var(--color-accent-primary-subtle)' : 'transparent',
                    color: active ? 'primary.main' : 'text.secondary',
                    '&:hover': {
                      bgcolor: 'var(--color-surface-glass-hover)',
                      color: 'text.primary',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 36,
                      color: 'inherit',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: { fontSize: '0.875rem', fontWeight: active ? 600 : 400 },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        width,
        minWidth: width,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: 'var(--color-bg-secondary)',
        transition: 'width var(--transition-normal)',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 10,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 64,
          px: collapsed ? 1.5 : 2,
          gap: 1.5,
        }}
      >
        <Box
          component="img"
          src="/symbiote-logo.png"
          alt="Symbiote Orchestrator"
          sx={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-md)',
            flexShrink: 0,
            objectFit: 'contain',
          }}
        />
        {!collapsed && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1.125rem',
              background:
                'linear-gradient(135deg, var(--color-text-primary), var(--color-accent-primary))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              whiteSpace: 'nowrap',
            }}
          >
            Symbiote
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: 'divider' }} />

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {renderNavSection('Platform', filterByRoles(platformNav, user?.roles ?? []))}
        {renderNavSection('Build', filterByRoles(buildNav, user?.roles ?? []))}
      </Box>

      <Divider sx={{ borderColor: 'divider' }} />

      {/* Bottom controls */}
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <NotificationBell />
          <Tooltip title={themeMode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <IconButton
              size="small"
              onClick={() => dispatch(toggleTheme())}
              sx={{ color: 'text.secondary' }}
            >
              {themeMode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </IconButton>
          </Tooltip>
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <IconButton
              size="small"
              onClick={() => dispatch(toggleSidebar())}
              sx={{ color: 'text.secondary' }}
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* User section */}
        {user && (
          <Box
            onClick={(e) => {
              setUserAnchor(e.currentTarget);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1,
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'var(--color-surface-glass-hover)' },
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b78d7, #62559a)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <User size={16} color="#fff" />
            </Box>
            {!collapsed && (
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }} noWrap>
                  {user.displayName || user.email}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontSize: '0.6875rem' }}
                  noWrap
                >
                  {user.roles[0] ?? 'User'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Popover
        open={Boolean(userAnchor)}
        anchorEl={userAnchor}
        onClose={() => {
          setUserAnchor(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, minWidth: 160 }}>
          <ListItemButton
            onClick={() => {
              void handleLogout();
              setUserAnchor(null);
            }}
            sx={{ borderRadius: 1, gap: 1.5 }}
          >
            <LogOut size={16} />
            <Typography variant="body2">Sign out</Typography>
          </ListItemButton>
        </Box>
      </Popover>
    </Box>
  );
}
